import type { FunctionArgs } from "convex/server"
import type { FilterValue } from "@/components/list/filter-bar"
import type { Id, api } from "@/lib/api"
import { isWellFormedText } from "../../../convex/lib/feedbackPayload"
import { MAX_FILTER_VALUES } from "../../../convex/lib/limits"
import type { NotificationKind } from "../../../convex/lib/validators"

/**
 * The inbox as the URL holds it: what narrows it, and the way from there to
 * what the server takes. Pure, so a link, a reload and the back button all
 * give the same inbox.
 */

/**
 * The types of notification the inbox can be narrowed to, each with the
 * kinds it stands for. A ticket and an issue assigned are one type: to the
 * reader both are "given to me".
 */
export const INBOX_TYPES = {
  assigned: ["assigned", "issue_assigned"],
  mentioned: ["mentioned"],
  commented: ["issue_commented"],
  changed: ["issue_changed"],
  replied: ["requester_replied"],
  drafted: ["reply_drafted"],
  update: ["project_update"],
  handoff: ["agent_handoff_failed"],
} as const satisfies Record<string, readonly NotificationKind[]>
export type InboxType = keyof typeof INBOX_TYPES
const TYPES = Object.keys(INBOX_TYPES) as InboxType[]

export const READ_STATES = ["unread", "read"] as const
export type ReadState = (typeof READ_STATES)[number]

/** The search params of the inbox. Every key is optional and none is ever an empty list. */
export type InboxSearch = {
  type?: InboxType[]
  /** Project slugs. */
  project?: string[]
  /** User ids: who caused it. */
  from?: string[]
  /** One of the two; both is the same as neither, and is not kept. */
  read?: ReadState
}

/** What `notifications.list` and `markAllRead` take as `filters`. */
export type InboxFilters = NonNullable<FunctionArgs<typeof api.notifications.list>["filters"]>

// Longer than any id or slug; only here to bound what the URL can carry.
const VALUE_MAX = 128
const isValue = (value: string) =>
  value.length > 0 && value.length <= VALUE_MAX && isWellFormedText(value)

/**
 * A list as the URL may carry it: an array, one value, or values between
 * commas. What is not accepted is left out, as is a repeat and whatever comes
 * after the most the server takes.
 */
function listOf<Value extends string>(
  raw: unknown,
  accepts: (value: string) => boolean,
): Value[] | undefined {
  const candidates: unknown[] =
    typeof raw === "string" ? raw.split(",") : Array.isArray(raw) ? raw : []
  const kept: string[] = []
  for (const candidate of candidates) {
    if (typeof candidate !== "string") continue
    const value = candidate.trim()
    if (!accepts(value) || kept.includes(value)) continue
    kept.push(value)
    if (kept.length === MAX_FILTER_VALUES) break
  }
  return kept.length > 0 ? (kept as Value[]) : undefined
}

/** `validateSearch` of the route: whatever the URL says, an inbox that can be drawn. */
export function parseInboxSearch(raw: Record<string, unknown>): InboxSearch {
  const search: InboxSearch = {}
  const given = listOf<InboxType>(raw.type, (value) => (TYPES as string[]).includes(value))
  // In the set's own order, whatever order they came in.
  if (given) search.type = TYPES.filter((type) => given.includes(type))
  const project = listOf<string>(raw.project, isValue)
  if (project) search.project = project
  const from = listOf<string>(raw.from, isValue)
  if (from) search.from = from
  if (raw.read === "unread" || raw.read === "read") search.read = raw.read
  return search
}

/**
 * The `filters` for a search, given the ids of the projects the reader can
 * open by slug. Null when the search can match nothing: every project it
 * names is one the reader cannot open, or no longer exists.
 */
export function filtersOf(
  search: InboxSearch,
  projectIds: ReadonlyMap<string, Id<"projects">>,
): InboxFilters | null {
  const filters: InboxFilters = {}
  if (search.type) filters.kind = search.type.flatMap((type) => INBOX_TYPES[type])
  if (search.project) {
    const ids = search.project.flatMap((slug) => projectIds.get(slug) ?? [])
    if (ids.length === 0) return null
    filters.projectId = ids
  }
  if (search.from) filters.actorId = search.from
  if (search.read) filters.unread = search.read === "unread"
  return filters
}

/** The search as `<FilterBar>` holds it: one list per filter. */
export function filterValueOf(search: InboxSearch): FilterValue {
  const value: FilterValue = {}
  if (search.type) value.type = search.type
  if (search.project) value.project = search.project
  if (search.from) value.from = search.from
  if (search.read) value.read = [search.read]
  return value
}

/**
 * The search for what `<FilterBar>` reports. Read state is one value: the
 * one just ticked replaces the other, and none is no filter.
 */
export function searchOfFilterValue(value: FilterValue, current: InboxSearch): InboxSearch {
  return parseInboxSearch({
    type: value.type,
    project: value.project,
    from: value.from,
    read: value.read?.find((state) => state !== current.read) ?? value.read?.[0],
  })
}
